<template>
  <!--
    Searchable select: type to filter, ↑/↓ + Enter or click to choose, Esc to close.

    <SearchSelect v-model="workerId" :items="workers" placeholder="Изберете служител" />

    Props: items (array of objects), value (the key of the selected item, v-model),
    itemKey / itemLabel (field names, default "_id" / "name"), placeholder, noResultsText, width.
    Emits: input (the key) and select (the item) - only when another item is chosen.
    Slot "item" ({ item, selected }) changes how a row in the list looks.
  -->
  <div class="search-select" :style="{ width: width }">
    <input
      ref="input"
      class="search-select-input"
      type="text"
      autocomplete="off"
      :value="open ? search : selectedLabel"
      :placeholder="open && selectedLabel ? selectedLabel : placeholder"
      @input="onInput"
      @focus="openList"
      @click="openList"
      @blur="open = false"
      @keydown.down.prevent="move(1)"
      @keydown.up.prevent="move(-1)"
      @keydown.enter.prevent="choose(filtered[index])"
      @keydown.esc="$event.target.blur()"
    />
    <ul v-if="open" ref="list" class="search-select-list">
      <li
        v-for="(item, i) in filtered"
        :key="item[itemKey]"
        :class="{ active: i === index, selected: item[itemKey] === value }"
        @mousedown.prevent="choose(item)"
        @mouseenter="index = i"
      >
        <slot name="item" :item="item" :selected="item[itemKey] === value">
          {{ item[itemLabel] }}
        </slot>
      </li>
      <li v-if="!filtered.length" class="no-results">{{ noResultsText }}</li>
    </ul>
  </div>
</template>

<script>
export default {
  name: "SearchSelect",
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    value: {
      default: null,
    },
    itemKey: {
      type: String,
      default: "_id",
    },
    itemLabel: {
      type: String,
      default: "name",
    },
    placeholder: {
      type: String,
      default: "Изберете",
    },
    noResultsText: {
      type: String,
      default: "Няма намерени резултати",
    },
    width: {
      type: String,
      default: "300px",
    },
  },
  data() {
    return {
      open: false,
      search: "",
      index: 0,
    };
  },
  computed: {
    selectedLabel() {
      const item = this.items.find((i) => i[this.itemKey] === this.value);
      return item ? item[this.itemLabel] : "";
    },
    // every word of the search is in the label, in any order
    filtered() {
      const words = this.search.toLowerCase().split(/\s+/).filter(Boolean);
      if (!words.length) return this.items;
      return this.items.filter((i) => {
        const label = String(i[this.itemLabel] || "").toLowerCase();
        return words.every((w) => label.includes(w));
      });
    },
  },
  methods: {
    onInput(e) {
      this.search = e.target.value;
      this.index = 0;
    },
    openList() {
      if (this.open) return;
      this.search = "";
      this.open = true;
      // start from the selected item
      this.index = Math.max(this.items.findIndex((i) => i[this.itemKey] === this.value), 0);
      this.scrollToActive();
    },
    move(step) {
      if (!this.open) return this.openList();
      const last = this.filtered.length - 1;
      if (last < 0) return;
      this.index = Math.min(Math.max(this.index + step, 0), last);
      this.scrollToActive();
    },
    scrollToActive() {
      this.$nextTick(() => {
        const item = this.$refs.list && this.$refs.list.children[this.index];
        if (item) item.scrollIntoView({ block: "nearest" });
      });
    },
    choose(item) {
      if (!item) return;
      this.open = false;
      this.$refs.input.blur();
      if (item[this.itemKey] === this.value) return;
      this.$emit("input", item[this.itemKey]);
      this.$emit("select", item);
    },
  },
};
</script>

<style scoped>
.search-select {
  position: relative;
}
/* dropdown arrow */
.search-select::after {
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid #555;
  content: "";
  pointer-events: none;
  position: absolute;
  right: 12px;
  top: 16px;
}
.search-select-input {
  background-color: #fff;
  border: 1px solid #c9d9d9;
  border-radius: 4px;
  box-sizing: border-box;
  height: 36px;
  padding: 0 30px 0 10px;
  width: 100%;
}
.search-select-input:focus {
  border-color: #083f75;
  outline: none;
}
.search-select-input::placeholder {
  color: #333;
}
.search-select-input:focus::placeholder {
  color: #9a9a9a;
}
.search-select-list {
  background-color: #fff;
  border: 1px solid #c9d9d9;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
  font-size: 13px;
  left: 0;
  list-style: none;
  margin: 2px 0 0;
  max-height: 320px;
  min-width: 100%;
  overflow-y: auto;
  padding: 4px 0;
  position: absolute;
  top: 100%;
  z-index: 20;
}
.search-select-list li {
  align-items: center;
  cursor: pointer;
  display: flex;
  padding: 6px 10px;
}
.search-select-list li.active {
  background-color: #e8eeee;
}
.search-select-list li.selected {
  font-weight: 700;
}
.search-select-list .no-results {
  color: #8a8a8a;
  cursor: default;
}
</style>
